// Voice dictation for the composer.
//
// The privacy caveat is the reason this file reads the way it does. iOS dictates with
// `SFSpeechRecognizer` and sets `requiresOnDeviceRecognition = true` whenever the device supports it,
// so the audio never leaves the phone — and the Worker's TTS proxy says outright that raw microphone
// audio is never sent to a server. The browser offers no equivalent: the Web Speech API in Chrome
// streams audio to Google, and in Safari to Apple. Neither is Oreia, and neither is covered by the
// promise the rest of the app keeps.
//
// So it is opt-in per session, behind a plain-language explanation, and it says where the audio goes
// and that the phone is the private way to do this. Nothing starts recording until the person has
// read that and pressed the button.

import { useCallback, useEffect, useRef, useState } from 'react';
import { IconClose, IconMic } from '../components/Icons';
import { PrimaryButton } from '../components/ui';

// Minimal shape of the Web Speech API. It is not in the DOM typings on every TypeScript version, and
// declaring only what is used keeps this from being a wall of ambient types.
interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  0: SpeechRecognitionAlternative;
  isFinal: boolean;
  length: number;
}
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: { length: number; [index: number]: SpeechRecognitionResult };
}
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event & { error?: string }) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function recognitionCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Whether this browser can dictate at all — Firefox, for one, cannot. */
export function canDictate(): boolean {
  return recognitionCtor() !== null;
}

/** Who transcribes the audio, so the warning can name them rather than say "a third party". */
function transcriber(): string {
  const ua = navigator.userAgent;
  const isSafari = /Safari/.test(ua) && !/Chrome|Chromium|Edg/.test(ua);
  return isSafari ? 'Apple' : 'Google';
}

export function VoiceDictationSheet({
  open,
  onClose,
  onInsert,
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the finished text, to append at the cursor. */
  onInsert: (text: string) => void;
}) {
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState('');
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recognition = useRef<SpeechRecognitionLike | null>(null);

  const stop = useCallback(() => {
    recognition.current?.stop();
    recognition.current = null;
    setListening(false);
    setInterim('');
  }, []);

  // Releases the microphone if the sheet is closed, the composer navigates away, or the tab is torn
  // down mid-sentence — a recogniser left running keeps the mic indicator lit.
  useEffect(() => () => stop(), [stop]);
  useEffect(() => {
    if (!open) stop();
  }, [open, stop]);

  const start = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor) {
      setError('This browser cannot dictate. Chrome, Edge and Safari can.');
      return;
    }

    setError(null);
    const rec = new Ctor();
    rec.lang = navigator.language || 'en-US';
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (event) => {
      let settled = '';
      let pending = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) settled += result[0].transcript;
        else pending += result[0].transcript;
      }
      if (settled) setHeard((current) => (current + ' ' + settled.trim()).trim());
      setInterim(pending);
    };

    rec.onerror = (event) => {
      const code = (event as { error?: string }).error;
      setError(
        code === 'not-allowed'
          ? 'Your browser blocked the microphone. Allow it in the address bar and try again.'
          : code === 'no-speech'
            ? 'Nothing came through — check the right microphone is selected.'
            : 'Dictation stopped unexpectedly.',
      );
      setListening(false);
    };

    // Chrome ends the session on its own after a pause; reflect that rather than showing a
    // recording state that is no longer true.
    rec.onend = () => setListening(false);

    recognition.current = rec;
    rec.start();
    setListening(true);
  }, []);

  if (!open) return null;

  const text = (heard + (interim ? ` ${interim}` : '')).trim();

  return (
    <>
      <div className="orset-scrim" onClick={onClose} />
      <div className="orset ovoice" role="dialog" aria-modal="true" aria-label="Dictate">
        <div className="orset-head">
          <h2>Dictate</h2>
          <button className="orset-x" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        <div className="orset-body">
          <p className="ovoice-warn">
            <strong>Your voice leaves this device.</strong> The browser sends what you say to{' '}
            {transcriber()} to turn it into text — not to Oreia, and not covered by the encryption
            protecting everything you write. For private dictation, use the iOS app, which transcribes
            on the phone itself and never uploads the audio.
          </p>

          {error ? <p className="ovoice-error">{error}</p> : null}

          <div className={`ovoice-stage${listening ? ' live' : ''}`}>
            {text ? (
              <p className="ovoice-text">
                {heard}
                {interim ? <span className="ovoice-interim"> {interim}</span> : null}
              </p>
            ) : (
              <p className="ovoice-idle">
                {listening ? 'Listening…' : 'Nothing yet. Press the microphone to begin.'}
              </p>
            )}
          </div>

          <div className="ovoice-actions">
            <button
              className={`ovoice-mic${listening ? ' on' : ''}`}
              onClick={listening ? stop : start}
              aria-pressed={listening}
            >
              <IconMic />
              {listening ? 'Stop' : 'Start dictation'}
            </button>
            <span className="ospacer" />
            {heard ? (
              <button className="olink-plain" onClick={() => setHeard('')}>
                Clear
              </button>
            ) : null}
            <PrimaryButton
              disabled={!heard.trim()}
              onClick={() => {
                stop();
                onInsert(heard.trim());
                setHeard('');
                onClose();
              }}
            >
              Add to entry
            </PrimaryButton>
          </div>
        </div>
      </div>
    </>
  );
}
